import type {
  AysCodeCodingSystem,
  AysCodingSystemClassification,
  AysCodingSystemSpecification,
} from '@bindoc-projects/code-domain-lib';
import {
  AYS_CODING_SYSTEM_SPECIFICATION_DISPLAY_NAMES
} from '@bindoc-projects/code-domain-lib';
import type { AysCodeSpectrumMatching } from '@bindoc-projects/code-spectrum-domain-lib';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { BdArrayUtil, BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionDto } from '@bindoc-projects/condition-domain-lib';
import { BdCondition } from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const CODE_SPECTRUM_CONDITION_ID = 'codeSpectrumId';
export const FilterConditionCodeSpectrumPluginType: FilterConditionPluginSpecification<
  FilterConditionCodeSpectrumObject,
  FilterConditionCodeSpectrumDto,
  FilterConditionCodeSpectrumConditionJson
> = {
  type: 'codes',
  category: 'case',
  displayName: { de: 'Ziffer-Spektra' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionCodeSpectrumDto,
  ): FilterConditionPluginImpl<FilterConditionCodeSpectrumObject> {
    const matching = obj.matching || 'exact';

    let codeTypeSpecification: AysCodingSystemSpecification;
    if (obj.codeTypeSpecification) {
      codeTypeSpecification = obj.codeTypeSpecification;
    }

    return new FilterConditionCodeSpectrumPlugin(
        obj.codeSpectrumIds || [],
        obj.codingSystem,
        matching,
        codeTypeSpecification,
        obj.codeTypeClassification,
        obj.includes,
        obj.grouperVersionYear,
      );
  },
  fromCondition(
    jsonObj: FilterConditionCodeSpectrumConditionJson,
  ): FilterConditionCodeSpectrumPlugin {
    return new FilterConditionCodeSpectrumPlugin(
        jsonObj.includes ?? jsonObj.excludes,
        jsonObj.codingSystem,
        jsonObj.matching,
        jsonObj.codeTypeSpecification,
        jsonObj.codeTypeClassification,
        !(jsonObj.excludes && Array.isArray(jsonObj.excludes)),
        jsonObj.grouperVersionYear,
      );
  },
  newInstance(): FilterConditionPluginImpl<FilterConditionCodeSpectrumObject> {
    return new FilterConditionCodeSpectrumPlugin([], 'OPS');
  },
};

export class FilterConditionCodeSpectrumPlugin
  extends FilterConditionPluginImpl<
    FilterConditionCodeSpectrumObject,
    FilterConditionCodeSpectrumDto
  >
  implements FilterConditionCodeSpectrumObject
{
  static override Specification: FilterConditionPluginSpecification<
    FilterConditionCodeSpectrumObject,
    FilterConditionCodeSpectrumDto,
    FilterConditionCodeSpectrumConditionJson
  > = FilterConditionCodeSpectrumPluginType;

  constructor(
    public codeSpectrumIds: string[],
    public codingSystem: AysCodeCodingSystem,
    public matching: AysCodeSpectrumMatching = 'exact',
    public codeTypeSpecification?: AysCodingSystemSpecification,
    public codeTypeClassification?: AysCodingSystemClassification,
    public includes = true,
    public grouperVersionYear?: number,
  ) {
    super(FilterConditionCodeSpectrumPluginType.type);
  }

  clone(): FilterConditionCodeSpectrumPlugin {
    return new FilterConditionCodeSpectrumPlugin(
      [...this.codeSpectrumIds],
      this.codingSystem,
      this.matching ? JSON.parse(JSON.stringify(this.matching)) : null,
      this.codeTypeSpecification
        ? JSON.parse(JSON.stringify(this.codeTypeSpecification))
        : null,
      this.codeTypeClassification
        ? JSON.parse(JSON.stringify(this.codeTypeClassification))
        : null,
      this.includes,
      this.grouperVersionYear,
    );
  }

  override equals(other: FilterConditionCodeSpectrumPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.codeSpectrumIds, other.codeSpectrumIds) &&
      BdEquals.equals(this.codingSystem, other.codingSystem) &&
      BdEquals.equals(this.matching, other.matching) &&
      BdEquals.equals(
        this.codeTypeClassification,
        other.codeTypeClassification,
        (a, b) =>
          BdEquals.equals(a?.name, b?.name) &&
          BdEquals.equals(a?.numberOfDigit, b?.numberOfDigit),
      ) &&
      BdEquals.equals(
        this.codeTypeSpecification,
        other.codeTypeSpecification,
        (a, b) => JSON.stringify(a) === JSON.stringify(b),
      ) &&
      BdEquals.equals(this.includes, other.includes) &&
      BdEquals.equals(this.grouperVersionYear, other.grouperVersionYear)
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    const codeSpectrumIds: string[] = [];

    this.codeSpectrumIds.forEach((codeSpectrumId) => {
      if (codeSpectrumIds.indexOf(codeSpectrumId) === -1) {
        codeSpectrumIds.push(codeSpectrumId);
      }
    });

    return { [ShareableType.CODE_SPECTRUM]: codeSpectrumIds };
  }

  override valid(): boolean {
    return (
      super.valid() && this.codeSpectrumIds && this.codeSpectrumIds.length > 0
    );
  }

  override validationToText(): string {
    if (this.codeSpectrumIds.length === 0) {
      return 'Mindestens ein Ziffer-Spektrum auswählen';
    } else {
      return '';
    }
  }

  override toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const mode = this.includes ? 'einschließend' : 'ausschließend';
    const matchingLabel = this.matching === 'leftAnchored' ? 'auflösend' : 'exakt';
    const header = `${this.codingSystem} ${mode} ${matchingLabel}`;
    const names = this.codeSpectrumIds.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    const items = names.length > 0 ? [`${header}: ${names.join(', ')}`] : [header];
    return { title, items };
  }

  toText(): string | null {
    const prefixInfo: string[] = [this.codingSystem.toString()];
    const suffixInfo: string[] = [];
    if (this.codeTypeSpecification) {
      suffixInfo.push(
        AYS_CODING_SYSTEM_SPECIFICATION_DISPLAY_NAMES[
          this.codeTypeSpecification
        ],
      );
    }
    if (this.codeTypeClassification) {
      suffixInfo.push(this.codeTypeClassification.name);
    }
    prefixInfo.push(this.includes ? 'einschließend' : 'ausschließend');
    if (this.matching) {
      switch (this.matching) {
        case 'exact': {
          prefixInfo.push('exakt');
          break;
        }
        case 'leftAnchored': {
          prefixInfo.push('auflösend');
          break;
        }
      }
    }

    const mainAndSuffix =
      suffixInfo.length > 0
        ? `Ziffer-Spektrum nach ${suffixInfo.join(' ')}`
        : 'Ziffer-Spektrum';

    if (this.codeSpectrumIds.length === 1) {
      return `ein ${prefixInfo.join(' ')} ${mainAndSuffix}`;
    } else {
      return `${this.codeSpectrumIds.length} ${prefixInfo.join(' ')} ${mainAndSuffix}`;
    }
  }

  toJSON(): FilterConditionCodeSpectrumDto | null {
    return {
      type: this.type,
      codeSpectrumIds: this.codeSpectrumIds,
      codingSystem: this.codingSystem,
      codeTypeSpecification: this.codeTypeSpecification,
      codeTypeClassification: this.codeTypeClassification,
      matching: this.matching,
      includes: this.includes,
      grouperVersionYear: this.grouperVersionYear,
    };
  }

  toValue(): FilterConditionCodeSpectrumObject {
    return this;
  }

  public toCondition(): BdCondition | null {
    return new FilterConditionCodeSpectrumCondition(this);
  }
}

export class FilterConditionCodeSpectrumCondition extends BdCondition {
  constructor(private _codeSpectrum: FilterConditionCodeSpectrumObject) {
    super(CODE_SPECTRUM_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        includes: this._codeSpectrum.includes ? this._codeSpectrum.codeSpectrumIds : undefined,
        excludes: this._codeSpectrum.includes ? undefined : this._codeSpectrum.codeSpectrumIds,
        codingSystem: this._codeSpectrum.codingSystem,
        matching: this._codeSpectrum.matching,
        grouperVersionYear: this._codeSpectrum.grouperVersionYear,
        codeTypeSpecification: this._codeSpectrum.codeTypeSpecification,
        codeTypeClassification: this._codeSpectrum.codeTypeClassification,
      }
    }
  }
}

interface FilterConditionCodeSpectrumObject {
  codeSpectrumIds: string[];
  codingSystem: AysCodeCodingSystem;
  matching: AysCodeSpectrumMatching;
  codeTypeSpecification?: AysCodingSystemSpecification;
  codeTypeClassification?: AysCodingSystemClassification;
  includes?: boolean;
  grouperVersionYear?: number;
}

interface FilterConditionCodeSpectrumDto
  extends FilterConditionCodeSpectrumObject {
  type: string;
}

export interface FilterConditionCodeSpectrumConditionJson
  extends Omit<FilterConditionCodeSpectrumObject, 'includes'> {
  includes: string[] | undefined;
  excludes: string[] | undefined;
}
